import React, { useState } from "react";
import { api } from "../lib/api";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Brutalist newsletter subscribe form.
 * `variant="footer"` → light, inline.
 * `variant="banner"` → black, high-contrast for the homepage banner.
 */
export const NewsletterForm = ({ variant = "footer", source = "footer", className = "" }) => {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post("/newsletter/subscribe", { email: email.trim(), source });
            setDone(true);
            if (data.already_subscribed) {
                toast(data.message || "Already on the list");
            } else if (data.mocked) {
                toast("Subscribed", { description: data.message || "Welcome email queued." });
            } else {
                toast.success("Subscribed", { description: `Welcome email sent to ${data.email}` });
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not subscribe");
        } finally {
            setBusy(false);
        }
    };

    const isBanner = variant === "banner";

    if (done) {
        return (
            <div
                data-testid={`newsletter-done-${variant}`}
                className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest ${
                    isBanner ? "text-[#CCFF00]" : "text-black"
                } ${className}`}
            >
                <CheckCircle2 className="w-4 h-4" />
                You're on the list — see you Friday.
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            data-testid={`newsletter-form-${variant}`}
            className={`flex flex-col sm:flex-row gap-2 ${className}`}
        >
            <div
                className={`flex-1 flex items-center border-2 ${
                    isBanner ? "border-[#CCFF00] bg-black" : "border-black bg-white"
                }`}
            >
                <span
                    className={`px-3 py-2 border-r-2 ${
                        isBanner ? "border-[#CCFF00] bg-[#CCFF00] text-black" : "border-black bg-[#FDFBF7]"
                    }`}
                >
                    <Mail className="w-4 h-4" />
                </span>
                <input
                    type="email"
                    required
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={`newsletter-email-${variant}`}
                    className={`flex-1 px-3 py-2 outline-none font-mono text-sm bg-transparent ${
                        isBanner ? "text-[#CCFF00] placeholder-[#CCFF00]/40" : "text-black"
                    }`}
                />
            </div>
            <button
                type="submit"
                disabled={busy}
                data-testid={`newsletter-submit-${variant}`}
                className={`px-4 py-2 font-display font-black uppercase tracking-widest text-xs border-2 inline-flex items-center justify-center gap-2 disabled:opacity-60 ${
                    isBanner
                        ? "bg-[#CCFF00] text-black border-[#CCFF00] shadow-[4px_4px_0_#FF00FF]"
                        : "bg-black text-[#CCFF00] border-black shadow-brutal-sm"
                }`}
            >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Get the Drop →
            </button>
        </form>
    );
};
